import * as React from 'react';
import styled from 'styled-components';
import {SystemIcons} from '../../icons';
import { BREAKPOINTS, COLORS, ComponentSStyling, ComponentTextStyle, ComponentXSStyling, Z_INDEXES } from '../../styles';
import { useLocation } from 'react-router-dom';
import { IconButton } from '../../Button';
import { LaerdalLogo } from '../../assets';
import {
  MenuAccountInfo,
  MenuButton,
  MenuNavigationItemTypeGroup,
  MenuGroupFooter,
  MenuGroupHeader,
  MenuIconButton,
  MenuNavigationItemTypeItem,
  MobileNavigationMenuProps,
  ProfileMenu,
  MenuNavigationItemTypeSection,
  MobileMenuButtonTypes,
  MenuSwitcherButton,
  MenuNavigationCustomSubMenu,
} from '../types';
import MobileMenuHeader from './MobileMenuHeader';
import MobileMenuContent from './MobileMenuContent';
import { useStackState } from '../../common/StackState';
import { useMediaMatch } from 'rooks';

const Wrapper = styled.div`
  width: 320px;
  z-index: ${Z_INDEXES.off_canvas};
  height: 100%;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  gap: 8px;

  ${BREAKPOINTS.SMALL} {
    width: 400px;
  }
`;

const SwitcherLabel = styled.div`
  margin-left: auto;
`;

const MobileFooter = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 48px;

  padding: 0 4px 0 16px;
  border-top: 1px solid ${COLORS.neutral_200};

  ${ComponentXSStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
  ${BREAKPOINTS.SMALL} {
    height: 56px;
    padding: 4px 8px 4px 16px;
    ${ComponentSStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
  }
`;

const CustomContent = styled.div`
  width: 100%;
  box-sizing: border-box;
  flex-flow: column;
  display: flex;
  flex: 1;
  overflow: hidden;
`;

type Props = MobileNavigationMenuProps & {
  profile?: ProfileMenu;
  show?: boolean;
  onClose: () => void;
};

interface MenuState {
  header: {
    label?: string;
    buttons?: MobileMenuButtonTypes[];
  };
  content?: {
    header?: MenuGroupHeader;
    items?: (MenuNavigationItemTypeItem | MenuNavigationItemTypeGroup | MenuNavigationItemTypeSection)[];
    footer?: MenuGroupFooter;
    action?: MenuButton;
  };
  customContent?: () => React.ReactNode;
  footer?: boolean;
}

const MobileMenu = ({ show, profile, buttons, label, header, items, footer, action, onClose }: Props) => {
  const { reset, length, push, pop, peek } = useStackState<MenuState>();

  const isMediumScreen = useMediaMatch(BREAKPOINTS.LARGE.replace('@media ', ''));

  const location = useLocation();

  const onSubMenuOpen = (menu?: MenuNavigationItemTypeGroup | MenuNavigationCustomSubMenu) => {
    switch (menu?.type) {
      case 'group':
        push({
          header: {
            label: menu.label,
            buttons: menu.buttons,
          },
          content: {
            header: menu.header,
            items: menu.items,
            footer: menu.footer,
            action: menu.action,
          },
          footer: false,
        });
        break;
      case 'custom':
        push({
          header: {
            label: menu.label,
            buttons: menu.buttons,
          },
          customContent: menu.custom,
          footer: false,
        });
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    onClose();
  }, [location]);

  React.useEffect(() => {
    isMediumScreen && onClose();
  }, [isMediumScreen]);

  React.useEffect(() => {
    setTimeout(() => {
      reset({
        footer: true,
        header: {
          label: label,
          buttons: buttons,
        },
        content: {
          header: header,
          items: items,
          footer: footer,
          action: action,
        },
      });
    }, 300);
  }, [show]);

  const current = peek();

  const switcher = current?.header?.buttons?.find((a) => a.type === 'switcher') as MenuSwitcherButton;

  return (
    <Wrapper>
      <MobileMenuHeader
        buttons={current?.header.buttons}
        onGoBack={length > 1 ? pop : undefined}
        onSubMenuOpen={onSubMenuOpen}
        profile={profile}
        label={current?.header.label}
        onClose={onClose}
      />
      {current?.customContent && <CustomContent>{current?.customContent()}</CustomContent>}
      {current?.content && (
        <MobileMenuContent
          header={current?.content.header}
          items={current?.content.items}
          footer={current?.content.footer}
          action={current?.content.action}
          onSubMenuOpen={onSubMenuOpen}
        />
      )}

      {current?.footer && (
        <MobileFooter>
          <LaerdalLogo height={'36px'} width={'66px'} />
          {switcher && (
            <>
              <SwitcherLabel className={'globalNav-toggler'} onClick={() => switcher.action && switcher.action() && onClose()}>
                {label || 'Switch site'}
              </SwitcherLabel>
              <IconButton variant={'secondary'} shape={'circular'} id="globalNavAppsButton" action={() => switcher.action && switcher.action() && onClose()}>
                <SystemIcons.SwitchApp size="24px"/>
              </IconButton>
            </>
          )}
        </MobileFooter>
      )}
    </Wrapper>
  );
};

export default MobileMenu;
